import React, { useEffect, useState } from 'react';
import { getAgents as getAgentsService } from 'services/modules/soc/agent';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, TablePagination, Divider } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

const ViewAgents = () => {
  // React Router
  const navigate = useNavigate();

  // State Variables
  const [agents, setAgents] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0); 

  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
 
  // Fetches agents based on group,soc env
  useEffect(() => {
    const fetchAgents = async () => {
      const params = {
        group: selectedCompanyContainer?.soc_group,
        soc_env: selectedCompanyContainer?.soc_env,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort: 'id',
      };

      try {
        const data = await getAgentsService(params);
        if (data && data.data && data.data.affected_items) {
          const agentData = data.data.affected_items.map((agent) => ({
            name: agent.name,
            ip: agent.ip,
            os: `${agent.os.name} (Version: ${agent.os.version})`,
            status: agent.status,
            enrollmentDate: agent.dateAdd.substring(0, 10),
            id: agent.id,
          }));
          setAgents(agentData);
          setTotalCount(data.data.total_affected_items || 0);
        }
      } catch (error) {
      } finally {
        setLoadingData(false);
      }
    };

    fetchAgents();
  }, [page, rowsPerPage, selectedCompanyContainer?.soc_env,selectedCompanyContainer?.soc_group]);
  
  // Navigates to individual agent dashboard based on agent id
  const viewAgents = (agent) => {
    navigate(`/soc/dashboard?agentname=${agent.name}&agentid=${agent.id}`);
  };
  
  // Handles Change in Page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handles change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  return (
    <div className="m-2">
      <div className="row header-cont p-2">
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-3 partner-cont">
          <img src="../assets/partners.svg" className="card-icon" alt="logo" />
          <span className="icon-color text-h5 text-bold font">View Agents</span>
        </div>
      </div>
      <Divider className="divider" />
      <TableContainer className="mx-2">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">Agent Name</TableCell>
              <TableCell align="center">IP Address</TableCell>
              <TableCell align="center">OS</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Enrollment Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingData ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress className="text-blueviolet" />
                </TableCell>
              </TableRow>
            ) : agents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <WarningIcon color="black" fontSize="small" />
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              agents.map((agent, index) => (
                <TableRow key={index}>
                  <TableCell align="center">{agent.name}</TableCell>
                  <TableCell align="center">{agent.ip}</TableCell>
                  <TableCell align="center">{agent.os}</TableCell>
                  <TableCell align="center" style={{ color: agent.status === 'active' ? 'green' : (agent.status === 'disconnected' ? 'red' : 'yellow') }}>
                    <b>{agent.status.substring(0, 1).toUpperCase() + agent.status.slice(1)}</b>
                  </TableCell>
                  <TableCell align="center">{agent.enrollmentDate}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Agent" placement="top">
                      <img 
                        src="../assets/viewicon.svg" 
                        alt="View" 
                        className='icons-style' 
                        onClick={() => viewAgents(agent)} 
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default ViewAgents;
